<script>
import { getPublicCard } from '@/utils/business-card';

const SOCIAL_ICONS = {
  linkedin: 'ti ti-brand-linkedin',
  instagram: 'ti ti-brand-instagram',
  facebook: 'ti ti-brand-facebook',
  twitter: 'ti ti-brand-x',
  youtube: 'ti ti-brand-youtube',
  website: 'ti ti-world',
};

export default {
  name: 'ViewBusinessCard',
  data() {
    return {
      loading: true,
      not_found: false,
      error_message: '',
      card: null,
      photo_failed: false, 
    };
  },

  computed: {
    theme_class() {
      return `theme-${this.card?.theme || 'modern'}`;
    },
    name() {
    return this.card?.name || '';
    },
    photo() {
    return this.card?.photo || null;
    },
    initial() {
      return (this.name || 'A').charAt(0).toUpperCase();
    },
    socials() {
      if (!this.show('social_links')) return [];
      return (this.card?.social_links || []).filter(s => s.url);
    },
    wa_link() {
      const digits = (this.card?.whatsapp || '').replace(/\D/g, '');
      if (!digits) return '';
      // Prefix India's country code for 10-digit numbers
      return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
    },
  },

  async mounted() {
    await this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await getPublicCard(
        this.$route.params.public_token,
        this.$route.query.src
        );
        const d = response.data.data || {};
        // Response is either { card: {...} } or the card object itself; handle both
        this.card = d;
        // Show the name in the browser tab title
        if (this.name) document.title = `${this.name} | Business Card`;
        } catch (error) {
        console.error('Public card error:', error);
        this.not_found = true;
        this.error_message = error.message || 'Card not available';
        } finally {
        this.loading = false;
      }
    },

    // Visible unless visibility is explicitly turned off
    show(key) {
      const v = this.card?.visibility;
      if (v && v[key] === false) return false;
      return true;
    },

    // Show only if the value exists and visibility is not turned off
    has(key) {
      return this.show(key) && !!this.card?.[key];
    },

    icon(type) {
      return SOCIAL_ICONS[type] || 'ti ti-link';
    },

    fixUrl(url) {
      if (!url) return '#';
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },

    async share() {
      const url = window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({ title: this.name, url });
        } else {
          await navigator.clipboard.writeText(url);
          this.$swalToast('Link copied');
        }
      } catch (e) {
        // User cancelled the share dialog, ignore
      }
    },

    // Build a .vcf file so the contact can be saved to the phone
    saveContact() {
    const base = process.env.VUE_APP_API_BASE_URL;
    window.location.href = `${base}/public/card/${this.$route.params.public_token}/vcard`;
    },
  },
};
</script>

<style scoped>
/* ── THEME VARIABLES ── */
.theme-modern {
  --bc-page-bg: #eef2f7;
  --bc-card-bg: #ffffff;
  --bc-cover: linear-gradient(135deg, #6366f1, #8b5cf6);
  --bc-text: #0f172a;
  --bc-muted: #64748b;
  --bc-body: #475569;
  --bc-accent: #6366f1;
  --bc-btn-bg: #6366f1;
  --bc-btn-hover: #4f46e5;
  --bc-btn-text: #ffffff;
  --bc-chip-bg: #f1f5f9;
  --bc-chip-hover: #e2e8f0;
  --bc-chip-text: #334155;
  --bc-border: #f1f5f9;
  --bc-icon: #94a3b8;
  --bc-avatar-border: #ffffff;
  --bc-avatar-bg: #e2e8f0;
  --bc-avatar-text: #475569;
  --bc-shadow: 0 10px 40px rgba(15, 23, 42, .12);
  --bc-name-font: inherit;
  --bc-radius: 20px;
}

.theme-classic {
  --bc-page-bg: #f4efe6;
  --bc-card-bg: #fffdf8;
  --bc-cover: linear-gradient(135deg, #1e293b, #334155);
  --bc-text: #1e293b;
  --bc-muted: #78716c;
  --bc-body: #57534e;
  --bc-accent: #b45309;
  --bc-btn-bg: #1e293b;
  --bc-btn-hover: #0f172a;
  --bc-btn-text: #fde68a;
  --bc-chip-bg: #f5f0e6;
  --bc-chip-hover: #ebe3d3;
  --bc-chip-text: #44403c;
  --bc-border: #ece5d6;
  --bc-icon: #a8a29e;
  --bc-avatar-border: #fffdf8;
  --bc-avatar-bg: #e7e0d0;
  --bc-avatar-text: #57534e;
  --bc-shadow: 0 8px 30px rgba(68, 64, 60, .15);
  --bc-name-font: Georgia, 'Times New Roman', serif;
  --bc-radius: 6px;
}

.theme-dark {
  --bc-page-bg: #0b1120;
  --bc-card-bg: #111827;
  --bc-cover: linear-gradient(135deg, #0ea5e9, #6366f1);
  --bc-text: #f1f5f9;
  --bc-muted: #94a3b8;
  --bc-body: #cbd5e1;
  --bc-accent: #38bdf8;
  --bc-btn-bg: #38bdf8;
  --bc-btn-hover: #0ea5e9;
  --bc-btn-text: #0b1120;
  --bc-chip-bg: #1f2937;
  --bc-chip-hover: #374151;
  --bc-chip-text: #e2e8f0;
  --bc-border: #1f2937;
  --bc-icon: #64748b;
  --bc-avatar-border: #111827;
  --bc-avatar-bg: #1f2937;
  --bc-avatar-text: #e2e8f0;
  --bc-shadow: 0 10px 40px rgba(0, 0, 0, .5);
  --bc-name-font: inherit;
  --bc-radius: 20px;
}

/* ── LAYOUT (theme-independent) ── */
.bc-page {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px 14px;
  background: var(--bc-page-bg);
}

.bc-card {
  width: 100%;
  max-width: 420px;
  background: var(--bc-card-bg);
  border-radius: var(--bc-radius);
  overflow: hidden;
  box-shadow: var(--bc-shadow);
}

.bc-cover { height: 110px; background: var(--bc-cover); }
.bc-accent { color: var(--bc-accent); }

.bc-avatar-wrap { display: flex; justify-content: center; margin-top: -48px; }
.bc-avatar {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 4px solid var(--bc-avatar-border);
  background: var(--bc-avatar-bg);
  object-fit: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 700;
  color: var(--bc-avatar-text);
}

.bc-body { padding: 14px 22px 24px; text-align: center; }
.bc-name { font-family: var(--bc-name-font); font-size: 21px; font-weight: 700; color: var(--bc-text); margin: 8px 0 2px; }
.bc-desig { font-size: 13.5px; color: var(--bc-muted); }
.bc-company { font-size: 13px; font-weight: 600; margin-top: 2px; }
.bc-bio { font-size: 13px; color: var(--bc-body); margin-top: 14px; line-height: 1.5; white-space: pre-line; }

.bc-actions { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 18px; }
.bc-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
  border-radius: 12px;
  background: var(--bc-chip-bg);
  color: var(--bc-chip-text);
  font-size: 11.5px;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
}
.bc-action i { font-size: 20px; }
.bc-action:hover { background: var(--bc-chip-hover); color: var(--bc-chip-text); }

.bc-info { margin-top: 16px; text-align: left; }
.bc-info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--bc-border);
  font-size: 13px;
  color: var(--bc-text);
  text-decoration: none;
  word-break: break-all;
}
.bc-info-row i { font-size: 18px; flex-shrink: 0; color: var(--bc-icon); }

.bc-socials { display: flex; justify-content: center; flex-wrap: wrap; gap: 10px; margin-top: 18px; }
.bc-social {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bc-chip-bg);
  color: var(--bc-chip-text);
  font-size: 19px;
  text-decoration: none;
}
.bc-social:hover { background: var(--bc-chip-hover); }

.bc-btn-main {
  width: 100%;
  margin-top: 20px;
  padding: 12px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--bc-btn-bg);
  color: var(--bc-btn-text);
}
.bc-btn-main:hover { background: var(--bc-btn-hover); }

.bc-state { text-align: center; padding: 60px 24px; color: var(--bc-muted); font-size: 14px; }
.bc-state i { font-size: 44px; display: block; margin-bottom: 10px; color: var(--bc-icon); }
</style>

<template>
    <div class="bc-page" :class="theme_class">
    <div class="bc-card">

      <!-- Loading -->
      <div v-if="loading" class="bc-state">Loading...</div>

      <!-- Not found / inactive -->
      <div v-else-if="not_found || !card" class="bc-state">
        <i class="ti ti-id-off"></i>
        This business card is not available.
      </div>

      <!-- Card -->
      <template v-else>
        <div class="bc-cover"></div>

        <div class="bc-avatar-wrap">
        <img v-if="photo && !photo_failed" :src="photo" class="bc-avatar" :alt="name"
            @error="photo_failed = true" />
        <div v-else class="bc-avatar">{{ initial }}</div>
        </div>

        <div class="bc-body">
          <div class="bc-name">{{ name }}</div>
          <div class="bc-desig" v-if="has('designation')">{{ card.designation }}</div>
          <div class="bc-company bc-accent" v-if="has('company')">{{ card.company }}</div>
          <div class="bc-desig" v-if="has('department')">{{ card.department }}</div>

          <div class="bc-bio" v-if="has('bio')">{{ card.bio }}</div>

          <!-- Quick actions -->
          <div class="bc-actions">
            <a v-if="has('phone')" :href="`tel:${card.phone}`" class="bc-action">
              <i class="ti ti-phone"></i>Call
            </a>
            <a v-if="has('whatsapp') && wa_link" :href="wa_link" target="_blank" rel="noopener" class="bc-action">
              <i class="ti ti-brand-whatsapp"></i>WhatsApp
            </a>
            <a v-if="has('email')" :href="`mailto:${card.email}`" class="bc-action">
              <i class="ti ti-mail"></i>Email
            </a>
          </div>

          <!-- Details -->
          <div class="bc-info">
            <a v-if="has('phone')" :href="`tel:${card.phone}`" class="bc-info-row">
              <i class="ti ti-phone"></i>{{ card.phone }}
            </a>
            <a v-if="has('email')" :href="`mailto:${card.email}`" class="bc-info-row">
              <i class="ti ti-mail"></i>{{ card.email }}
            </a>
            <a v-if="has('website')" :href="fixUrl(card.website)" target="_blank" rel="noopener" class="bc-info-row">
              <i class="ti ti-world"></i>{{ card.website }}
            </a>
          </div>

          <!-- Socials -->
          <div class="bc-socials" v-if="socials.length">
            <a v-for="(s, i) in socials" :key="i" :href="fixUrl(s.url)" target="_blank" rel="noopener"
              class="bc-social" :title="s.type">
              <i :class="icon(s.type)"></i>
            </a>
          </div>

          <button type="button" class="bc-btn-main" @click="saveContact">
            <i class="ti ti-user-plus"></i>Save Contact
          </button>
          <button type="button" class="bc-action mt-2 w-100 flex-row justify-content-center" style="flex-direction:row;" @click="share">
            <i class="ti ti-share"></i>Share
          </button>
        </div>
      </template>

    </div>
  </div>
</template>